import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    BatterySetDto,
    BatterySetService,
    CompleteMissionCommand,
    CompleteSortieCommand,
    CraftService,
    DisplayableMissionDto,
    EquipmentDto,
    EquipmentService,
    FlyFreelyError,
    FlyFreelyLoggingService,
    MaintenanceLogDto,
    MaintenanceService,
    MissionRoleDto,
    MissionRoleService,
    MissionService,
    RpaDto,
    RpaTypeDto,
    RpaTypesService,
    ServiceabilitySignoffCommand,
    ServiceabilitySignoffDto,
    SimplePersonDto,
    SortieDto,
    WorkTracker,
    fromTimestamp,
    prepareResponses,
    toTimestamp
} from '@flyfreely-portal-ui/flyfreely';
import { deepEqual } from 'fast-equals';
import * as moment from 'moment';
import { ReplaySubject, Subject, forkJoin } from 'rxjs';
import {
    distinctUntilChanged,
    mergeMap,
    take,
    takeUntil
} from 'rxjs/operators';
import { checkFlightsSorted, getEffectiveStartTime } from '../helpers';
import { MissionCompletionOrRecordService } from './unassigned-mission-flight-logs/interfaces';

export interface MissionCompletionFormModel {
    outcome: FormControl<CompleteMissionCommand.Outcome>;
    objectiveOutcomeNotes: FormControl<string>;
    actualStartTime: FormControl<Date>;
    actualEndTime: FormControl<Date>;
    actualLocationId: FormControl<number>;
    journal: FormControl<string>;
    documentation: FormControl<any>;
    flights: FormArray<FormControl<CompleteSortieCommand>>;
    serviceabilitySignoffs: FormArray<FormGroup<ServiceabilitySignoffForm>>;
    maintenanceLogs: FormControl<MaintenanceLogDto[]>;
    serviceability: FormControl<ServiceabilitySignoffDto[]>;
}

export interface MissionCompletionFormValue {
    outcome: CompleteMissionCommand.Outcome;
    objectiveOutcomeNotes: string;
    actualStartTime: Date;
    actualEndTime: Date;
    actualLocationId: number;
    journal: string;
    documentation: any;
    flights: CompleteSortieCommand[];
    serviceabilitySignoffs: ServiceabilitySignoffFields[];
    maintenanceLogs: MaintenanceLogDto[];
    serviceability: ServiceabilitySignoffDto[];
}

export interface DisplayableResource {
    id: number;
    name: string;
}

export interface ServiceabilitySignoffForm {
    rpaId: FormControl<number>;
    unlistedRpaId: FormControl<string>;
    name: FormControl<string>;
    serviceability: FormControl<ServiceabilitySignoffCommand.Serviceability>;
    reason: FormControl<string>;
    signoffPerson: FormControl<SimplePersonDto>;
    signoffTime: FormControl<string>;
    flown: FormControl<boolean>;
    maintenanceLogId: FormControl<number>;
}

export interface ServiceabilitySignoffFields {
    rpaId: number;
    unlistedRpaId: string;
    name: string;
    serviceability: ServiceabilitySignoffCommand.Serviceability;
    reason: string;
    signoffPerson: SimplePersonDto;
    signoffTime: string;
    flown: boolean;
    maintenanceLogId: number;
}

@Injectable()
export class MissionCompletionService extends MissionCompletionOrRecordService<MissionCompletionFormModel> {
    public form: FormGroup<MissionCompletionFormModel>;

    private allRpasSubject = new ReplaySubject<RpaDto[]>();
    private allRpaTypesSubject = new ReplaySubject<RpaTypeDto[]>();
    private allBatterySetsSubject = new ReplaySubject<BatterySetDto[]>();
    private allEquipmentSubject = new ReplaySubject<EquipmentDto[]>();
    private availableRpaSubject = new ReplaySubject<RpaDto[]>();
    private availablePersonnelSubject = new ReplaySubject<
        DisplayableResource[]
    >();
    private availableBatterySetsSubject = new ReplaySubject<BatterySetDto[]>();
    private availableEquipmentSubject = new ReplaySubject<EquipmentDto[]>();
    private allUnlistedLinkedSubject = new ReplaySubject<boolean>();
    private allMaintenanceSubmittedSubject = new ReplaySubject<boolean>();
    private missionRolesSubject = new ReplaySubject<MissionRoleDto[]>(1);

    allRpas$ = this.allRpasSubject.asObservable();
    allRpaTypes$ = this.allRpaTypesSubject.asObservable();
    allBatterySets$ = this.allBatterySetsSubject.asObservable();
    allEquipment$ = this.allEquipmentSubject.asObservable();

    availableRpas$ = this.availableRpaSubject.asObservable();
    availablePersonnel$ = this.availablePersonnelSubject.asObservable();
    availableBatterySets$ = this.availableBatterySetsSubject.asObservable();
    availableEquipment$ = this.availableEquipmentSubject.asObservable();

    allUnlistedLinked$ = this.allUnlistedLinkedSubject.asObservable();
    allMaintenanceSubmitted$ =
        this.allMaintenanceSubmittedSubject.asObservable();
    missionRoles$ = this.missionRolesSubject.asObservable();

    canUseServiceabilitySignoffs = false;
    flightsSorted = true;

    private savedMissionSubject = new ReplaySubject<DisplayableMissionDto>(1);
    savedMission$ = this.savedMissionSubject.asObservable();

    private get flights() {
        return this.form.controls.flights;
    }

    private workTracker = new WorkTracker();
    private stopWatching$ = new Subject<void>();
    private ngUnsubscribe$ = new Subject<void>();

    override working$ = this.workTracker.observable;

    constructor(
        private missionService: MissionService,
        private craftService: CraftService,
        private rpaTypeService: RpaTypesService,
        private batterySetService: BatterySetService,
        private equipmentService: EquipmentService,
        private maintenanceService: MaintenanceService,
        private missionRoleService: MissionRoleService,
        private commonDialoguesService: CommonDialoguesService,
        private logging: FlyFreelyLoggingService
    ) {
        super();
        this.form = new FormGroup<MissionCompletionFormModel>({
            outcome: new FormControl(undefined, [Validators.required]),
            objectiveOutcomeNotes: new FormControl(undefined),
            actualStartTime: new FormControl(undefined),
            actualEndTime: new FormControl(undefined),
            journal: new FormControl(undefined),
            actualLocationId: new FormControl(undefined),
            documentation: new FormControl(undefined, Validators.required),
            flights: new FormArray([]),
            serviceabilitySignoffs: new FormArray([]),
            maintenanceLogs: new FormControl(undefined),
            serviceability: new FormControl(undefined)
        });
        
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.stopWatching$.next();
        this.stopWatching$.complete();
        this.allRpasSubject.complete();
        this.allRpaTypesSubject.complete();
        this.allBatterySetsSubject.complete();
        this.availableRpaSubject.complete();
        this.availablePersonnelSubject.complete();
        this.availableBatterySetsSubject.complete();
        this.allUnlistedLinkedSubject.complete();
        this.allMaintenanceSubmittedSubject.complete();
        this.missionRolesSubject.complete();
        this.savedMissionSubject.complete();
    }

    setCanUseServiceabilitySignoffs(canUse: boolean) {
        this.canUseServiceabilitySignoffs = canUse;
    }

    setupMission(mission: DisplayableMissionDto, organisationId: number) {
        this.stopWatching$.next();

        this.refreshLists(mission, organisationId);
        this.form.patchValue({
            outcome: mission.outcome,
            journal: mission.completionMessage,
            objectiveOutcomeNotes: mission.objectiveOutcomeNotes,
            actualLocationId: mission.actualLocationId || mission.locationId,
            actualStartTime: fromTimestamp(
                mission.actualStartTime ?? mission.missionDate,
                mission.timeZone
            ),
            actualEndTime: fromTimestamp(
                mission.actualEndTime ??
                    moment(mission.missionDate)
                        .add(mission.missionEstimatedTime, 'seconds')
                        .toISOString(),
                mission.timeZone
            ),
            maintenanceLogs: mission.maintenanceLogs,
            serviceability: mission.serviceabilitySignoffs
        });

        while (this.form.controls.flights.length > 0) {
            this.form.controls.flights.removeAt(0);
        }

        mission.sorties.forEach(f => this.addFlight(f));
        this.flightsSorted = checkFlightsSorted(this.flights.value);
        this.flights.valueChanges
            .pipe(takeUntil(this.stopWatching$))
            .subscribe(values => {
                this.flightsSorted = checkFlightsSorted(values);
            });
        if (this.canUseServiceabilitySignoffs) {
            this.refreshServiceabilitySignoffs(mission);
        }

        this.allUnlistedLinkedSubject.next(
            mission.unlistedBatterySets.reduce(
                (acc, u) => (acc && u.batterySetId != null) || u.ignored,
                true
            ) &&
                mission.unlistedPersonnel.reduce(
                    (acc, u) => (acc && u.personId != null) || u.ignored,
                    true
                ) &&
                mission.unlistedRpas.reduce(
                    (acc, u) => (acc && u.rpaId != null) || u.ignored,
                    true
                ) &&
                mission.unlistedEquipment.reduce(
                    (acc, u) => (acc && u.equipmentId != null) || u.ignored,
                    true
                )
        );

        this.refreshMaintenanceSubmitted();

        this.form.markAsPristine();

        if (this.canUseServiceabilitySignoffs) {
            this.form.controls.flights.valueChanges
                .pipe(
                    distinctUntilChanged(deepEqual),
                    takeUntil(this.stopWatching$)
                )
                .subscribe(() => this.refreshServiceabilitySignoffs(mission));
        }

        this.savedMissionSubject.next(mission);
    }

    refreshLists(mission: DisplayableMissionDto, organisationId: number) {
        forkJoin([
            this.craftService.findCrafts(organisationId),
            this.rpaTypeService.findRpaTypes(organisationId),
            this.batterySetService.findBatterySets(
                organisationId,
                mission.missionDate
            ),
            this.equipmentService.find(organisationId),
            this.missionRoleService.findMissionRoles(organisationId)
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: ([craft, rpaTypes, batterySets, equipment, roles]) => {
                    this.allRpasSubject.next(craft);
                    this.allRpaTypesSubject.next(rpaTypes);
                    this.allBatterySetsSubject.next(batterySets);
                    this.allEquipmentSubject.next(equipment);
                    this.missionRolesSubject.next(roles);
                    this.updateAvailableRpas(mission, craft);
                    this.updateAvailablePersonnel(mission);
                    this.updateAvailableBatterySets(batterySets);
                    this.updateAvailableEquipment(mission, equipment);
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while fetching resources: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    refreshServiceabilitySignoffs(mission: DisplayableMissionDto) {
        while (this.form.controls.serviceabilitySignoffs.length > 0) {
            this.form.controls.serviceabilitySignoffs.removeAt(0);
        }

        const formSignoffs = this.form.controls.serviceabilitySignoffs;
        const sorties = this.form.controls.flights.value;
        mission.crafts.map(craft => {
            const signoff =
                mission.serviceabilitySignoffs?.find(
                    s => s.rpaId != null && s.rpaId === craft.id
                ) ?? null;
            const flown = this.checkIfFlown(sorties, craft.id);
            if (signoff == null) {
                const command = new FormGroup<ServiceabilitySignoffForm>({
                    name: new FormControl(craft.nickname),
                    rpaId: new FormControl(craft.id, Validators.required),
                    reason: new FormControl(),
                    serviceability: new FormControl(),
                    unlistedRpaId: new FormControl(),
                    signoffPerson: new FormControl(),
                    signoffTime: new FormControl(),
                    flown: new FormControl(flown),
                    maintenanceLogId: new FormControl()
                });
                if (flown === true) {
                    command.controls.serviceability.setValidators(
                        Validators.required
                    );
                    command.controls.serviceability.updateValueAndValidity();
                }
                formSignoffs.push(command);
            } else {
                const reason = this.findServiceabilityReason(
                    signoff.maintenanceLogId,
                    mission.maintenanceLogs
                );
                const serviceability = signoff.serviceability ?? null;
                const command = new FormGroup<ServiceabilitySignoffForm>({
                    name: new FormControl(craft.nickname),
                    rpaId: new FormControl(craft.id, Validators.required),
                    reason: new FormControl(reason),
                    serviceability: new FormControl(serviceability),
                    unlistedRpaId: new FormControl(),
                    signoffPerson: new FormControl(signoff.signoffPerson),
                    signoffTime: new FormControl(signoff.signoffTime),
                    flown: new FormControl(flown),
                    maintenanceLogId: new FormControl(signoff.maintenanceLogId)
                });
                if (flown === true) {
                    command.controls.serviceability.setValidators(
                        Validators.required
                    );
                    command.controls.serviceability.updateValueAndValidity();
                }
                formSignoffs.push(command);
            }
        });
        mission.unlistedRpas.map(craft => {
            if (
                craft.rpaId != null &&
                mission.crafts.findIndex(r => r.id === craft.rpaId) === -1
            ) {
                const signoff =
                    mission.serviceabilitySignoffs.find(
                        s =>
                            s.rpaId != null &&
                            (s.unlistedRpaId === craft.id ||
                                s.rpaId === craft.rpaId)
                    ) ?? null;
                const flown = this.checkIfFlown(
                    sorties,
                    craft.rpaId ?? null,
                    craft.id
                );
                if (signoff == null) {
                    const command = new FormGroup<ServiceabilitySignoffForm>({
                        name: new FormControl(craft.name),
                        rpaId: new FormControl(
                            craft.rpaId ?? null,
                            Validators.required
                        ),
                        reason: new FormControl(),
                        serviceability: new FormControl(),
                        unlistedRpaId: new FormControl(craft.id),
                        signoffPerson: new FormControl(),
                        signoffTime: new FormControl(),
                        flown: new FormControl(flown),
                        maintenanceLogId: new FormControl()
                    });
                    if (flown === true) {
                        command.controls.serviceability.setValidators(
                            Validators.required
                        );
                        command.controls.serviceability.updateValueAndValidity();
                    }
                    formSignoffs.push(command);
                } else {
                    const reason = this.findServiceabilityReason(
                        signoff.maintenanceLogId,
                        mission.maintenanceLogs
                    );
                    const serviceability = signoff.serviceability ?? null;
                    const command = new FormGroup<ServiceabilitySignoffForm>({
                        name: new FormControl(craft.name),
                        rpaId: new FormControl(
                            craft.rpaId ?? null,
                            Validators.required
                        ),
                        reason: new FormControl(reason),
                        serviceability: new FormControl(serviceability),
                        unlistedRpaId: new FormControl(craft.id),
                        signoffPerson: new FormControl(signoff.signoffPerson),
                        signoffTime: new FormControl(signoff.signoffTime),
                        flown: new FormControl(flown),
                        maintenanceLogId: new FormControl(
                            signoff.maintenanceLogId
                        )
                    });
                    if (flown === true) {
                        command.controls.serviceability.setValidators(
                            Validators.required
                        );
                        command.controls.serviceability.updateValueAndValidity();
                    }
                    formSignoffs.push(command);
                }
            }
        });
    }

    checkIfFlown(
        sorties: SortieDto[] | CompleteSortieCommand[],
        craftId: number,
        unlistedCraftId?: string
    ) {
        const flown: boolean =
            sorties.findIndex(s => {
                if (s.craftId != null && craftId != null) {
                    return s.craftId === craftId;
                } else if (
                    s.unlistedRpa?.id != null &&
                    unlistedCraftId != null
                ) {
                    return s.unlistedRpa.id === unlistedCraftId;
                } else {
                    return -1;
                }
            }) !== -1;
        return flown;
    }

    refreshMaintenanceSubmitted() {
        if (this.form.controls.maintenanceLogs.value != null) {
            this.allMaintenanceSubmittedSubject.next(
                this.form.controls.maintenanceLogs.value.reduce(
                    (acc, l) =>
                        acc &&
                        (l.status === MaintenanceLogDto.Status.COMPLETED ||
                            l.status === MaintenanceLogDto.Status.CANCELLED ||
                            l.status === MaintenanceLogDto.Status.REQUESTED),
                    true
                )
            );
        } else {
            this.allMaintenanceSubmittedSubject.next(true);
        }
    }

    findServiceabilityReason(
        maintenanceId?: number,
        maintenanceLogs?: MaintenanceLogDto[]
    ) {
        if (
            maintenanceId == null ||
            maintenanceLogs == null ||
            maintenanceLogs.length === 0
        ) {
            return null;
        }
        const log = maintenanceLogs.find(l => l.id === maintenanceId);
        return log.reasonForRequest;
    }

    updateAvailableRpas(mission: DisplayableMissionDto, allRpas: RpaDto[]) {
        const assignedRpas = mission.unlistedRpas.filter(
            r =>
                r.rpaId != null &&
                mission.crafts.findIndex(c => c.id === r.rpaId) === -1
        );

        const linked = assignedRpas
            .map(c => allRpas.find(r => r.id === c.rpaId))
            .filter(c => c != null);

        const available = mission.crafts.concat(linked);
        this.availableRpaSubject.next(available);
    }

    updateAvailablePersonnel(mission: DisplayableMissionDto) {
        const assignedPersonnel = mission.unlistedPersonnel.filter(
            p =>
                p.personId != null &&
                mission.missionCrewDetails.findIndex(
                    pers => pers.person.id === p.personId
                ) === -1
        );
        const missionPersonnel = assignedPersonnel
            .map(person => {
                // person.personId is expected to be the linked person's numeric ID. Parse and validate it;
                // parseInt does not throw on invalid input, so explicitly handle NaN values.
                const id = Number(person.personId);
                if (person.personId == null || Number.isNaN(id)) {
                    return null;
                }
                return {
                    id: id,
                    name: `${person.firstName} ${person.lastName}`
                };
            })
            .filter(person => person != null);

        const available = mission.missionCrewDetails
            .map(person => ({
                id: person.person.id,
                name: `${person.person.firstName} ${person.person.lastName}`
            }))
            .concat(missionPersonnel);
        this.availablePersonnelSubject.next(available);
    }

    updateAvailableBatterySets(batterySets: BatterySetDto[]) {
        this.availableBatterySetsSubject.next(batterySets);
    }

    updateAvailableEquipment(
        mission: DisplayableMissionDto,
        allEquipment: EquipmentDto[]
    ) {
        const assigned = mission.unlistedEquipment.filter(
            r =>
                r.equipmentId != null &&
                mission.equipment.findIndex(eq => eq.id === r.equipmentId) ===
                    -1
        );

        const linked = assigned
            .map(c => allEquipment.find(eq => eq.id === c.equipmentId))
            .filter(c => c != null);

        const available = mission.equipment.concat(linked);
        this.availableEquipmentSubject.next(available);
    }

    updateFlights(flights: SortieDto[]) {
        flights.forEach((f, ix) => {
            const value = this.flights.controls[ix].value;
            if (f.durationSource === SortieDto.DurationSource.LOGS) {
                this.flights.controls[ix].patchValue({
                    ...value,
                    durationSource: f.durationSource,
                    duration: f.duration
                });
            } else {
                this.flights.controls[ix].patchValue({
                    ...value,
                    durationSource: f.durationSource
                });
            }
        });
    }

    updateFlight(flight: SortieDto) {
        if (
            flight == null ||
            this.flights == null ||
            this.flights.length === 0
        ) {
            return;
        }
        for (let ix = 0; ix < this.flights.length; ix++) {
            const currentValue = this.flights.at(ix).value;
            if (flight.id === currentValue.id) {
                this.flights.at(ix).setValue({
                    ...currentValue,
                    status: flight.status,
                    craftId: flight.craftId,
                    duration: flight.duration,
                    durationSource: flight.durationSource,
                    batterySetId: flight.batterySetId
                });
                return;
            }
        }
        // FIXME maybe create the flight if it doesn't exist?
    }

    updateMissionFromSortieCommand(
        sortieCommand: CompleteSortieCommand[],
        mission: DisplayableMissionDto
    ) {
        const value = this.form.value;
        const command: CompleteMissionCommand = {
            actualStartTime: toTimestamp(
                value.actualStartTime,
                mission.timeZone
            ),
            actualEndTime: toTimestamp(value.actualEndTime, mission.timeZone),
            actualLocationId: mission.locationId,
            formResponses:
                value.documentation != null
                    ? prepareResponses(value.documentation.formResponses)
                    : {},
            message: value.journal,
            outcome: value.outcome,
            objectiveOutcomeNotes: value.objectiveOutcomeNotes,
            sorties: sortieCommand
        };

        return this.missionService.completeMission(mission.id, command);
    }

    updateMaintenanceLog(log: MaintenanceLogDto) {
        const logs = this.form.controls.maintenanceLogs.value;
        const index = logs.findIndex(l => l.id === log.id);
        logs[index] = log;
        this.form.patchValue({
            maintenanceLogs: logs
        });
        this.refreshMaintenanceSubmitted();
    }

    private createFlightControl(s: SortieDto) {
        return new FormControl<CompleteSortieCommand>(
            // @ts-ignore - missing overlap is non-breaking for this call
            s,
            [Validators.required]
        );
    }

    addFlight(s: SortieDto): void {
        const flights = this.form.controls.flights;
        flights.push(this.createFlightControl(s));
    }

    deleteFlight(index: number) {
        this.form.controls.flights.removeAt(index);
    }

    deleteAllFlights() {
        while (this.form.controls.flights.length > 0) {
            this.form.controls.flights.removeAt(0);
        }
    }

    sortFlights(): void {
        const flights = this.form.controls.flights;
        const sorted = [...flights.controls].sort((a, b) => {
            const timeA = getEffectiveStartTime(a.value);
            const timeB = getEffectiveStartTime(b.value);
            if (timeA == null && timeB == null) {
                return 0;
            }
            if (timeA == null) {
                return 1;
            }
            if (timeB == null) {
                return -1;
            }
            return timeA.getTime() - timeB.getTime();
        });
        flights.clear();
        sorted.forEach(c => flights.push(c));
    }

    submitMaintenanceRequest(id: number) {
        this.commonDialoguesService.showConfirmationDialogue(
            'Submit Maintenance Request',
            'Are you sure you wish to submit this maintenance request?',
            'Submit',
            () =>
                new Promise<void>(res => {
                    this.savedMissionSubject
                        .pipe(
                            take(1),
                            mergeMap(mission =>
                                this.maintenanceService.submitMaintenanceLog(
                                    id,
                                    {
                                        managingOrganisationId:
                                            mission.organisationId
                                    }
                                )
                            ),
                            takeUntil(this.ngUnsubscribe$)
                        )
                        .subscribe({
                            next: log => {
                                this.updateMaintenanceLog(log);
                                this.logging.success(
                                    'Maintenance Request Submitted'
                                );
                            },
                            error: (error: FlyFreelyError) =>
                                this.logging.error(
                                    error,
                                    `Error submitting maintenance: ${error.message}`
                                )
                        })
                        .add(this.workTracker.createTracker());
                    res();
                })
        );
    }

    cancelMaintenanceRequest(id: number) {
        this.commonDialoguesService.showConfirmationDialogue(
            'Cancel Maintenance Request',
            'Are you sure you wish to cancel this maintenance request?',
            'Cancel',
            () =>
                new Promise<void>(res => {
                    this.savedMissionSubject
                        .pipe(
                            take(1),
                            mergeMap(mission =>
                                this.maintenanceService.cancelMaintenanceLog(
                                    id,
                                    {
                                        managingOrganisationId:
                                            mission.organisationId
                                    }
                                )
                            ),
                            takeUntil(this.ngUnsubscribe$)
                        )
                        .subscribe({
                            next: log => {
                                this.updateMaintenanceLog(log);
                                this.logging.success(
                                    'Maintenance Request Cancelled'
                                );
                            },
                            error: (error: FlyFreelyError) =>
                                this.logging.error(
                                    error,
                                    `Error cancelling maintenance: ${error.message}`
                                )
                        })
                        .add(this.workTracker.createTracker());
                    res();
                })
        );
    }
}
